/**
 * AiRangePanel — forward-range bands, on the tab that needs them.
 *
 * WHY IT MOVED HERE
 *
 * This lived on the Edge AI page, two clicks and a different mental model away from the Option
 * Desk — which is the one surface in the app that has to answer "how far is this likely to move,
 * and by when". Strike selection, expiry choice and every break-even judgement are that question.
 *
 * It is also the part of the Edge engine that most clearly earns its place. Unlike the ranked
 * board, these bands report their own MEASURED coverage against a stated target: an 80% band whose
 * realised hit rate is 77% is a calibrated instrument, and one reading 55% would be visibly broken.
 * The board had no equivalent — it reported a training-set lift and a probability, and its realised
 * direction accuracy turned out to be within noise of its own baseline.
 *
 * WHAT THE COLUMNS ARE FOR
 *
 * The learned band next to the vol-implied band is the whole point: the vol band is what the option
 * market has already priced, so a learned band INSIDE it says the model thinks the market is paying
 * for more movement than is likely, and a learned band outside says the opposite. That comparison
 * is what turns a range into a trade.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Button, Tooltip, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import { edgeService } from '../../services/edge.service';
import HelpLink from '../common/HelpLink';
import toast from 'react-hot-toast';

const fmtPx = (n: number) => (n >= 1000 ? Math.round(n).toLocaleString('en-US') : n.toFixed(2));
const pctS = (r: number) => `${r >= 0 ? '+' : ''}${(r * 100).toFixed(1)}%`;

const AiRangePanel: React.FC = () => {
  const { C } = useAppTheme();
  const [range, setRange] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    edgeService.getRange()
      .then((r: any) => setRange(r?.data?.data ?? null))
      .catch(() => setRange(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const rebuild = async () => {
    setBusy(true);
    try { await edgeService.rangeRebuild(); toast.success('Range rebuilt'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Rebuild failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;

  const symbols: any[] = range?.symbols || [];

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>How far it is likely to move</Typography>
        <Tooltip arrow title="Quantile models trained on BTC/ETH options history — positioning (put-call ratio, gamma exposure, max pain, the ATM straddle) plus technicals. The 80% range is the 10th to 90th percentile of the forward move. Unlike a ranked pick, a band can be checked: 'coverage' is how often the realised close actually landed inside it on held-out data, against a target of 80%.">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, cursor: 'help' }} />
        </Tooltip>
        <HelpLink to="ai-desk/options" variant="icon" label="How the range bands are built" />
        <Box sx={{ flex: 1 }} />
        <Tooltip arrow title="Recomputes the bands from the latest options history. Deterministic — no model call, so it costs nothing.">
          <span>
            <Button size="small" variant="outlined" disabled={busy} onClick={rebuild}>
              {busy ? 'Rebuilding…' : 'Rebuild'} · ₹0
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="caption" sx={{ color: C.dim, display: 'block', mb: 1.2, lineHeight: 1.5 }}>
        Compare the learned band with the <b>vol band</b> beside it. The vol band is the move the
        option market has already priced; a learned band inside it says these premiums are paying
        for more movement than is likely, and a band outside says they are not paying enough. Until
        a horizon has a trained model it shows the vol band alone, marked <b>IV-only</b>.
      </Typography>

      {!symbols.length ? (
        <Typography variant="caption" sx={{ color: C.dim }}>
          No range data yet — it builds from BTC/ETH options history on the next cycle, or press
          Rebuild to seed it now.
        </Typography>
      ) : symbols.map((s: any) => (
        <Box key={s.symbol} sx={{
          mb: 1.5, p: 1.2, borderRadius: 1, border: `1px solid ${C.neutralBorder}`, bgcolor: C.neutralMuted,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
            {s.symbol} · spot {fmtPx(s.spot)}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    ['Horizon', 'How far ahead this band looks.', 'left'],
                    ['80% range', 'The 10th to 90th percentile of the forward move, as prices. Eight times out of ten the close should land inside this.', 'right'],
                    ['Median', 'The middle of the forward distribution — the model’s central expectation, not a target.', 'right'],
                    ['Exp. move', 'Half the width of the 80% band, as a percentage. The single number to compare against an option’s break-even distance.', 'right'],
                    ['Vol band', 'The move the option market has already priced, from realised or implied volatility. This is the benchmark the learned band has to beat to be worth anything.', 'right'],
                    ['Model', 'Version, measured coverage against the 80% target, and the sample it was trained on. Coverage well below 80% means the band is too narrow to trust.', 'left'],
                  ].map(([h, hint, align]) => (
                    <Tooltip key={h as string} arrow title={hint as string}>
                      <TableCell align={align as any} sx={{ fontWeight: 800, whiteSpace: 'nowrap', cursor: 'help' }}>
                        {h}
                      </TableCell>
                    </Tooltip>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(s.horizons || []).map((h: any) => {
                  const q = h.quantiles || {};
                  const lowRet = q['0.1'], highRet = q['0.9'], medRet = q['0.5'];
                  const hasBand = lowRet != null && highRet != null;
                  const low = hasBand ? s.spot * (1 + lowRet) : null;
                  const high = hasBand ? s.spot * (1 + highRet) : null;
                  const med = medRet != null ? s.spot * (1 + medRet) : null;
                  const expMove = hasBand ? (highRet - lowRet) / 2 : (h.ivImpliedMove || 0);
                  const ivm = h.ivImpliedMove || 0;
                  const cov = h.metrics?.coverage80;
                  // A band whose measured coverage is far from its 80% target is not a band, it is
                  // a number with a confidence interval attached to it by assertion. Flagged rather
                  // than hidden, because the model column is where a reader decides whether to use it.
                  const covOff = cov != null && Math.abs(cov - 0.8) > 0.1;
                  return (
                    <TableRow key={h.horizon}>
                      <TableCell sx={{ fontWeight: 700 }}>{h.horizon}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {hasBand ? `${fmtPx(low as number)} – ${fmtPx(high as number)}` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: medRet != null ? (medRet >= 0 ? C.bull : C.bear) : C.dim,
                      }}>
                        {med != null ? `${fmtPx(med as number)} (${pctS(medRet)})` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        ±{(expMove * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right" sx={{ color: C.dim, fontVariantNumeric: 'tabular-nums' }}>
                        {ivm ? `${fmtPx(s.spot * (1 - ivm))} – ${fmtPx(s.spot * (1 + ivm))}` : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {h.modelVersion ? (
                          <Tooltip arrow title={
                            cov == null
                              ? 'No held-out coverage measured for this horizon yet.'
                              : `The realised close landed inside this 80% band ${Math.round(cov * 100)}% of the time on held-out data, trained on ${h.trainSamples} samples.` +
                                (covOff ? ' That is more than ten points from the 80% target, so this band is not yet calibrated — read it as indicative.' : '')
                          }>
                            <span style={{ cursor: 'help' }}>
                              v{h.modelVersion} · cov{' '}
                              <span style={{ color: covOff ? C.warn : C.bull, fontWeight: 800 }}>
                                {cov != null ? `${Math.round(cov * 100)}%` : '—'}
                              </span>
                              <span style={{ color: C.dim }}> (n{h.trainSamples})</span>
                            </span>
                          </Tooltip>
                        ) : h.modelRejectedReason ? (
                          // A model EXISTS and was refused at predict time. That is a different
                          // situation from never having trained one, and it needs a different
                          // response — a retrain, not patience — so it does not share the chip.
                          <Tooltip arrow title={
                            `A trained model exists for this horizon but was refused for this prediction: ${h.modelRejectedReason}. ` +
                            'A band several times the market’s own implied move covers almost everything and cannot pick a strike, ' +
                            'so the vol band is shown instead. This clears on the next retrain that produces a plausible band.'
                          }>
                            <Chip size="small" label="model refused"
                              sx={{ height: 16, fontSize: '0.55rem', cursor: 'help', bgcolor: C.warnMuted, color: C.warn }} />
                          </Tooltip>
                        ) : (
                          <Tooltip arrow title="No trained model for this horizon yet, so the vol-implied band is shown on its own.">
                            <Chip size="small" label="IV-only" sx={{ height: 16, fontSize: '0.55rem', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default AiRangePanel;
